<template>
    <base-tool
        :tool-name="tool.name"
        :tool-description="tool.description"
        @back="handleBack"
    >
        <!-- Loading state -->
        <div v-if="loading" class="text-center py-3">
            <i class="el-icon-loading"></i> Loading...
        </div>

        <!-- Create / Edit form view -->
        <div v-else-if="showForm">
            <el-form label-position="top" size="small">
                <el-form-item label="Title" required>
                    <el-input
                        v-model="form.name"
                        placeholder="e.g. Sales Appointments"
                        data-testid="appointment-config-name-input"
                    />
                </el-form-item>

                <el-form-item label="Assign appointments to this user" required>
                    <user-selector
                        :value="form.user_id"
                        :multiple="false"
                        :hide_extensions="true"
                        data-testid="appointment-config-user-selector"
                        @change="(val) => form.user_id = val"
                    />
                </el-form-item>

                <el-form-item label="Allowed Durations (minutes)" required>
                    <el-select
                        v-model="form.allowed_durations"
                        multiple
                        placeholder="Select durations"
                        class="w-100"
                        data-testid="appointment-config-durations"
                    >
                        <el-option
                            v-for="d in availableDurations"
                            :key="d"
                            :label="d + ' min'"
                            :value="d"
                        />
                    </el-select>
                </el-form-item>

                <el-form-item>
                    <template #label>
                        <span>Allowed Operations</span>
                        <el-tooltip placement="top" effect="dark">
                            <div slot="content" style="max-width: 280px;">
                                Select which operations the AloAi agent can perform.<br>
                                Leave empty to allow all operations.
                            </div>
                            <i class="el-icon-question ml-1" style="color: #909399; cursor: help;"></i>
                        </el-tooltip>
                    </template>
                    <el-checkbox-group v-model="form.allowed_operations_selected">
                        <el-checkbox label="create">Create</el-checkbox>
                        <el-checkbox label="update">Update</el-checkbox>
                        <el-checkbox label="cancel">Cancel</el-checkbox>
                        <el-checkbox label="list" :disabled="listRequired">List</el-checkbox>
                    </el-checkbox-group>
                    <small v-if="listRequired" class="text-muted">
                        List is required when Update or Cancel is selected.
                    </small>
                </el-form-item>

                <el-form-item>
                    <el-checkbox
                        v-model="form.allow_overlap"
                        data-testid="appointment-config-overlap"
                    >
                        Allow overlapping appointments
                    </el-checkbox>
                </el-form-item>

                <el-form-item label="Default Timezone">
                    <el-select
                        v-model="form.default_timezone"
                        filterable
                        clearable
                        placeholder="No default (AloAi will ask)"
                        class="w-100"
                        data-testid="appointment-config-timezone"
                    >
                        <el-option
                            v-for="tz in timezones"
                            :key="tz"
                            :label="tz"
                            :value="tz"
                        />
                    </el-select>
                </el-form-item>

                <el-form-item>
                    <template #label>
                        <span>Title Template</span>
                        <el-tooltip placement="top" effect="dark">
                            <div slot="content" style="max-width: 280px;">
                                Define a template for appointment titles.<br>
                                Use [Variable] placeholders that will be replaced with actual values.<br>
                                Leave empty to let the AloAi agent generate the title.
                            </div>
                            <i class="el-icon-question ml-1" style="color: #909399; cursor: help;"></i>
                        </el-tooltip>
                    </template>
                    <div class="d-flex align-items-center">
                        <el-input
                            ref="titleTemplateInput"
                            v-model="form.title_template"
                            placeholder="e.g. Appointment with [FirstName] [LastName]"
                            class="flex-grow-1"
                            @blur="saveTitleCursorPosition"
                            data-testid="appointment-config-title-template"
                        />
                        <variables
                            class="ml-1"
                            @variable-selected="insertTitleVariable"
                        />
                    </div>
                </el-form-item>

                <el-form-item>
                    <template #label>
                        <span>Allowed Scheduling Times</span>
                        <small class="text-muted d-block">
                            Times are local to your company timezone
                            <b v-if="current_company">({{ current_company.timezone }})</b>
                        </small>
                    </template>
                    <business-hours
                        v-if="allowedTimesFixed && form.allowed_times"
                        type="select"
                        color="#00BF50"
                        :days="form.allowed_times"
                        :time-increment="timeIncrement"
                    />
                </el-form-item>

                <div class="d-flex justify-content-end mt-2">
                    <el-button
                        v-if="configs.length > 0"
                        size="small"
                        @click="cancelForm"
                    >
                        Cancel
                    </el-button>
                    <el-button
                        type="primary"
                        size="small"
                        :loading="saving"
                        @click="saveConfig"
                    >
                        {{ editingConfigId ? 'Save' : 'Save & Select' }}
                    </el-button>
                </div>
            </el-form>
        </div>

        <!-- Table list view -->
        <div v-else>
            <div class="config-list" style="max-height: 280px; overflow-y: auto;">
                <div
                    v-for="config in configs"
                    :key="config.id"
                    class="config-row d-flex align-items-center justify-content-between p-2 mb-1 border rounded"
                    :class="{ 'config-row--hover': !deleting && !cloning }"
                >
                    <div
                        class="config-name flex-grow-1"
                        style="cursor: pointer; min-width: 0;"
                        @click="selectConfig(config)"
                    >
                        <span class="text-truncate d-block">{{ config.name }}</span>
                        <small class="text-muted">{{ config.user_name }}</small>
                    </div>
                    <div class="config-actions flex-shrink-0 ml-2">
                        <el-button
                            type="text"
                            size="mini"
                            icon="el-icon-copy-document"
                            title="Clone"
                            :loading="cloning === config.id"
                            @click.stop="cloneConfig(config)"
                        />
                        <el-button
                            type="text"
                            size="mini"
                            icon="el-icon-edit"
                            title="Edit"
                            @click.stop="editConfig(config)"
                        />
                        <el-button
                            type="text"
                            size="mini"
                            icon="el-icon-delete"
                            class="text-danger"
                            title="Delete"
                            :loading="deleting === config.id"
                            @click.stop="deleteConfig(config)"
                        />
                    </div>
                </div>
            </div>

            <div class="text-center mt-2">
                <el-button
                    type="text"
                    size="small"
                    icon="el-icon-plus"
                    @click="startCreate"
                >
                    Create New
                </el-button>
            </div>
        </div>
    </base-tool>
</template>

<script>
import { mapState } from 'vuex'
import UserSelector from '../../../user-selector'
import Variables from '../../../messenger/variables.vue'
import BaseTool from './base-tool.vue'
import ToolMixin from './tool-mixin'

const DEFAULT_OPERATING_HOURS = {
    monday: [{ open: '24hrs', close: '24hrs', id: '1', isOpen: true }],
    tuesday: [{ open: '24hrs', close: '24hrs', id: '2', isOpen: true }],
    wednesday: [{ open: '24hrs', close: '24hrs', id: '3', isOpen: true }],
    thursday: [{ open: '24hrs', close: '24hrs', id: '4', isOpen: true }],
    friday: [{ open: '24hrs', close: '24hrs', id: '5', isOpen: true }],
    saturday: [{ open: '24hrs', close: '24hrs', id: '6', isOpen: true }],
    sunday: [{ open: '24hrs', close: '24hrs', id: '7', isOpen: true }]
}

export default {
    name: 'AppointmentTool',

    mixins: [ToolMixin],

    components: {
        BaseTool,
        UserSelector,
        Variables
    },

    props: {
        tool: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            configs: [],
            loading: true,
            showForm: false,
            saving: false,
            deleting: null,
            cloning: null,
            editingConfigId: null,
            titleCursorPosition: 0,
            allowedTimesFixed: false,
            isFixingAllowedTimes: false,
            timeIncrement: 30,
            availableDurations: [15, 30, 45, 60, 90, 120],
            timezones: Intl.supportedValuesOf('timeZone'),
            form: this.emptyForm()
        }
    },

    computed: {
        ...mapState('cache', ['current_company']),

        listRequired() {
            const ops = this.form.allowed_operations_selected
            return ops.includes('update') || ops.includes('cancel')
        }
    },

    watch: {
        'form.allowed_operations_selected'(ops) {
            if ((ops.includes('update') || ops.includes('cancel')) && !ops.includes('list')) {
                ops.push('list')
            }
        }
    },

    mounted() {
        this.fetchConfigs()
    },

    methods: {
        emptyForm() {
            return {
                name: '',
                user_id: null,
                allowed_durations: [15, 30, 60],
                allowed_operations_selected: [],
                allow_overlap: false,
                default_timezone: null,
                title_template: '',
                allowed_times: JSON.parse(JSON.stringify(DEFAULT_OPERATING_HOURS))
            }
        },

        fetchConfigs() {
            this.loading = true
            axios.get('/api/v1/aloai/manage-appointments')
                .then(response => {
                    this.configs = response.data.data || []
                    if (this.configs.length === 0) {
                        this.showForm = true
                        this.fixAllowedTimes()
                    }
                })
                .catch(error => {
                    console.error('Failed to fetch manage appointments:', error)
                })
                .finally(() => {
                    this.loading = false
                })
        },

        selectConfig(config) {
            const configText = `{Manage appointment with: ${config.name}|${config.id}}`
            this.$emit('appointment-selected', { text: configText })
        },

        startCreate() {
            this.editingConfigId = null
            this.form = this.emptyForm()
            this.showForm = true
            this.$nextTick(() => {
                this.fixAllowedTimes()
            })
        },

        editConfig(config) {
            this.editingConfigId = config.id
            this.form = {
                name: config.name,
                user_id: config.user_id,
                allowed_durations: config.allowed_durations || [15, 30, 60],
                allowed_operations_selected: config.allowed_operations || [],
                allow_overlap: config.allow_overlap || false,
                default_timezone: config.default_timezone || null,
                title_template: config.title_template || '',
                allowed_times: config.allowed_times
                    ? JSON.parse(JSON.stringify(config.allowed_times))
                    : JSON.parse(JSON.stringify(DEFAULT_OPERATING_HOURS))
            }
            this.showForm = true
            this.$nextTick(() => {
                this.fixAllowedTimes()
            })
        },

        cloneConfig(config) {
            this.cloning = config.id
            axios.post(`/api/v1/aloai/manage-appointments/${config.id}/clone`)
                .then(response => {
                    const cloned = response.data.data
                    this.configs.push(cloned)
                    this.$message.success('Cloned successfully.')
                    this.editConfig(cloned)
                })
                .catch(error => {
                    const message = error.response?.data?.message || 'Failed to clone.'
                    this.$message.error(message)
                })
                .finally(() => {
                    this.cloning = null
                })
        },

        deleteConfig(config) {
            this.deleting = config.id
            axios.delete(`/api/v1/aloai/manage-appointments/${config.id}`)
                .then(() => {
                    this.configs = this.configs.filter(c => c.id !== config.id)
                    this.$message.success('Deleted successfully.')
                    if (this.configs.length === 0) {
                        this.startCreate()
                    }
                })
                .catch(error => {
                    const message = error.response?.data?.message || 'Failed to delete.'
                    this.$message.error(message)
                })
                .finally(() => {
                    this.deleting = null
                })
        },

        saveConfig() {
            if (!this.form.name || !this.form.name.trim()) {
                this.$message.error('Please enter a title.')
                return
            }

            if (!this.form.user_id) {
                this.$message.error('Please select a user.')
                return
            }

            if (!this.form.allowed_durations || this.form.allowed_durations.length === 0) {
                this.$message.error('Please select at least one duration.')
                return
            }

            this.saving = true

            const payload = {
                name: this.form.name,
                user_id: this.form.user_id,
                allowed_durations: this.form.allowed_durations,
                allowed_operations: this.form.allowed_operations_selected.length > 0
                    ? this.form.allowed_operations_selected
                    : null,
                allow_overlap: this.form.allow_overlap,
                default_timezone: this.form.default_timezone || null,
                title_template: this.form.title_template || null,
                allowed_times: this.form.allowed_times
            }

            const request = this.editingConfigId
                ? axios.put(`/api/v1/aloai/manage-appointments/${this.editingConfigId}`, payload)
                : axios.post('/api/v1/aloai/manage-appointments', payload)

            request
                .then(response => {
                    const config = response.data.data

                    if (this.editingConfigId) {
                        const index = this.configs.findIndex(c => c.id === this.editingConfigId)
                        if (index !== -1) {
                            this.$set(this.configs, index, config)
                        }
                        this.$message.success('Updated successfully.')
                    } else {
                        this.configs.push(config)
                        this.$message.success('Created successfully.')
                        this.selectConfig(config)
                        this.showForm = false
                        this.form = this.emptyForm()
                    }
                })
                .catch(error => {
                    const message = error.response?.data?.message || 'Failed to save.'
                    this.$message.error(message)
                })
                .finally(() => {
                    this.saving = false
                })
        },

        cancelForm() {
            this.showForm = false
            this.editingConfigId = null
            this.allowedTimesFixed = false
            this.form = this.emptyForm()
        },

        handleBack() {
            if (this.showForm && this.configs.length > 0) {
                this.cancelForm()
                return
            }
            this.goBack()
        },

        saveTitleCursorPosition() {
            const input = this.$refs.titleTemplateInput?.$refs?.input
            if (input) {
                this.titleCursorPosition = input.selectionStart || 0
            }
        },

        insertTitleVariable(variable) {
            let content = this.form.title_template || ''
            content = content.slice(0, this.titleCursorPosition) + variable + content.slice(this.titleCursorPosition)
            this.titleCursorPosition = this.titleCursorPosition + variable.length
            this.form.title_template = content
        },

        fixAllowedTimes() {
            if (this.isFixingAllowedTimes) {
                return
            }

            if (!this.form.allowed_times) {
                return
            }

            this.isFixingAllowedTimes = true
            this.allowedTimesFixed = false

            try {
                for (let day of ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']) {
                    if (!this.form.allowed_times[day]) {
                        continue
                    }

                    for (let item in this.form.allowed_times[day]) {
                        if (!this.form.allowed_times[day][item].open) {
                            this.form.allowed_times[day][item].open = ''
                        }

                        if (!this.form.allowed_times[day][item].close) {
                            this.form.allowed_times[day][item].close = ''
                        }

                        if (this.form.allowed_times[day][item].isOpen) {
                            if (this.form.allowed_times[day][item].open === '') {
                                this.form.allowed_times[day][item].open = '24hrs'
                            }

                            if (this.form.allowed_times[day][item].close === '') {
                                this.form.allowed_times[day][item].close = '24hrs'
                            }

                            if (this.form.allowed_times[day][item].open !== '24hrs' && this.form.allowed_times[day][item].close === '24hrs') {
                                this.form.allowed_times[day][item].open = '24hrs'
                            }
                        }
                    }
                }

                this.form.allowed_times = {
                    monday: this.form.allowed_times.monday,
                    tuesday: this.form.allowed_times.tuesday,
                    wednesday: this.form.allowed_times.wednesday,
                    thursday: this.form.allowed_times.thursday,
                    friday: this.form.allowed_times.friday,
                    saturday: this.form.allowed_times.saturday,
                    sunday: this.form.allowed_times.sunday,
                }

                this.allowedTimesFixed = true
            } finally {
                this.isFixingAllowedTimes = false
            }
        }
    }
}
</script>

<style scoped>
.config-row {
    transition: background-color 0.15s;
}

.config-row--hover:hover {
    background-color: #f5f7fa;
}

.config-name:hover span {
    color: #409EFF;
}
</style>
